import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from "recharts";
import {
  FileText,
  MessageSquareQuote,
  Code2,
  Briefcase,
  Trophy,
  ArrowUpRight,
  Plus,
  Sparkles,
  Eye,
  CheckCircle,
  Loader2,
  Activity,
  BarChart3
} from "lucide-react";

const PIE_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(var(--primary))"
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover/95 backdrop-blur px-3 py-2 shadow-md">
      {label && <p className="text-xs font-semibold mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="font-mono text-foreground">{p.value}</span> {p.name}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good morning");
    else if (hour < 18) setGreeting("Good afternoon");
    else setGreeting("Good evening");
  }, []);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await api.get("/Dashboard/summary");
        if (response.success) {
          setData(response.data);
        } else {
          toast.error("Failed to load dashboard data");
        }
      } catch (error) {
        toast.error("Error connecting to server");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const summaryCards = [
    {
      label: "Total Blogs",
      value: data.totalBlogs,
      icon: FileText,
      accent: "from-blue-500 to-indigo-500",
      iconBg: "bg-blue-500/10 text-blue-500",
    },
    {
      label: "Published",
      value: data.totalPublishedBlogs,
      icon: CheckCircle,
      accent: "from-emerald-500 to-teal-500",
      iconBg: "bg-emerald-500/10 text-emerald-500",
    },
    {
      label: "Blog Views",
      value: data.totalBlogViews,
      icon: Eye,
      accent: "from-amber-500 to-orange-500",
      iconBg: "bg-amber-500/10 text-amber-500",
    },
    {
      label: "Achievements",
      value: data.totalAchievements,
      icon: Trophy,
      accent: "from-yellow-400 to-amber-500",
      iconBg: "bg-yellow-500/10 text-yellow-600",
    },
    {
      label: "Experiences",
      value: data.totalExperiences,
      icon: Briefcase,
      accent: "from-purple-500 to-pink-500",
      iconBg: "bg-purple-500/10 text-purple-500",
    },
    {
      label: "Skills",
      value: data.totalSkills,
      icon: Code2,
      accent: "from-cyan-500 to-blue-500",
      iconBg: "bg-cyan-500/10 text-cyan-600",
    },
    {
      label: "Testimonials",
      value: data.totalTestimonials,
      icon: MessageSquareQuote,
      accent: "from-rose-500 to-red-500",
      iconBg: "bg-rose-500/10 text-rose-500",
    }
  ];

  // Map backend property to name/value for recharts
  const skillDistributionData = data.skillDistribution?.map(s => ({
    name: s.category,
    value: s.count
  })) || [];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Hero banner */}
      <Card className="relative overflow-hidden border-foreground/10 bg-foreground text-background p-0">
        <div className="absolute inset-0 grid-bg opacity-[0.07]" />
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/30 blur-3xl animate-pulse" />
        <div className="absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-chart-2/20 blur-3xl" />

        <div className="relative p-6 sm:p-8 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-background/15 bg-background/5 backdrop-blur text-[10px] font-mono uppercase tracking-wider">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live Dashboard
            </div>
            <div>
              <h2 className="font-display text-3xl sm:text-[40px] font-bold tracking-tight leading-tight">
                {greeting}, <span className="text-primary">{user?.username || user?.firstName || 'Admin'}</span>
              </h2>
              <p className="text-sm text-background/70 mt-2 max-w-xl leading-relaxed">
                Here's a summary of your portfolio metrics and achievements.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {summaryCards.map((s, index) => {
          const Icon = s.icon;
          return (
            <Card
              key={s.label}
              data-testid={`summary-card-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:bg-card hover:border-primary/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Animated gradient background on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${s.accent} opacity-0 group-hover:opacity-[0.08] transition-opacity duration-500`} />
              
              {/* Decorative blurred shapes */}
              <div className={`absolute -right-4 -top-4 h-16 w-16 rounded-full ${s.iconBg} opacity-20 group-hover:opacity-40 group-hover:scale-150 blur-xl transition-all duration-700 ease-out`} />
              <div className={`absolute -bottom-6 -left-6 h-12 w-12 rounded-full ${s.iconBg} opacity-10 group-hover:opacity-30 group-hover:scale-150 blur-lg transition-all duration-700 ease-out delay-100`} />
              
              <div className="relative p-4 flex flex-col h-full justify-between">
                <div className="flex items-start justify-between mb-3">
                  <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${s.iconBg} backdrop-blur-md border border-white/5 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500 shadow-sm`}>
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </div>
                  <div className="h-6 w-6 rounded-full bg-foreground/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
                    <ArrowUpRight className="h-3 w-3 text-foreground/70" />
                  </div>
                </div>
                
                <div>
                  <div className="font-display text-2xl sm:text-3xl font-black tracking-tight text-foreground group-hover:text-primary transition-colors duration-300">
                    {s.value}
                  </div>
                  <p className="text-[10px] font-bold text-muted-foreground mt-1 tracking-wider uppercase opacity-80 truncate">{s.label}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Charts & Quick Actions row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Skills Distribution Chart */}
        <Card className="p-6 lg:col-span-2 shadow-md hover:shadow-lg transition-shadow duration-300" data-testid="skills-distribution-chart">
          <div className="mb-6">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
               <Code2 className="h-5 w-5 text-chart-3" />
               Skills Distribution
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Breakdown of skills by category</p>
          </div>
          
          {skillDistributionData.length > 0 ? (
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-full sm:w-1/2 h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={skillDistributionData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      strokeWidth={0}
                    >
                      {skillDistributionData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} className="hover:opacity-80 transition-opacity cursor-pointer" />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="w-full sm:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {skillDistributionData.map((s, i) => (
                  <div key={s.name} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors">
                    <span className="h-3 w-3 rounded-full shadow-sm" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-sm font-medium text-foreground">{s.name}</span>
                    <span className="ml-auto font-mono text-sm text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
             <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                No skill data available
             </div>
          )}
        </Card>

        {/* Quick Actions */}
        <Card className="p-6 shadow-md hover:shadow-lg transition-shadow duration-300" data-testid="quick-actions">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-5 w-5 text-primary" strokeWidth={1.75} />
            <h3 className="font-display text-lg font-semibold">Quick Actions</h3>
          </div>
          <div className="space-y-3">
            {[
              { label: "New Blog Post", icon: FileText, testid: "quick-new-blog", color: "text-chart-1", bg: "bg-chart-1/10", path: "/blogs" },
              { label: "Add Testimonial", icon: MessageSquareQuote, testid: "quick-add-testimonial", color: "text-chart-2", bg: "bg-chart-2/10", path: "/testimonials" },
              { label: "Update Skill", icon: Code2, testid: "quick-update-skill", color: "text-chart-3", bg: "bg-chart-3/10", path: "/skills" },
              { label: "Add Achievement", icon: Trophy, testid: "quick-add-achievement", color: "text-chart-5", bg: "bg-chart-5/10", path: "/achievements" }
            ].map((a, idx) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.label}
                  data-testid={a.testid}
                  onClick={() => navigate(a.path)}
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-xl border border-border hover:border-primary/30 hover:bg-muted/40 transition-all text-left group overflow-hidden relative"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className={`h-10 w-10 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 ${a.bg} ${a.color} relative z-10`}>
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <span className="text-sm font-semibold flex-1 relative z-10 group-hover:text-primary transition-colors">{a.label}</span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition-all relative z-10" strokeWidth={2} />
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Day-wise Visits (Last 30 Days) */}
        <Card className="p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
          <div className="mb-6">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
               <BarChart3 className="h-5 w-5 text-chart-1" />
               Daily Visits (Last 30 Days)
            </h3>
          </div>
          <div className="w-full h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.dayWiseVisits || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{fill: 'hsl(var(--muted))'}} />
                <Bar dataKey="count" name="Visits" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Month-wise Visits (Last 12 Months) */}
        <Card className="p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
          <div className="mb-6">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
               <Activity className="h-5 w-5 text-primary" />
               Monthly Visits (Last 12 Months)
            </h3>
          </div>
          <div className="w-full h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.monthWiseVisits || []}>
                <defs>
                  <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="count" name="Visits" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorVisits)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}

